/**
 * Prepaid text credit (docs/PRICING.md): email is free, texts are paid for by the organizer
 * from account credit, one credit per text (every text is one segment). The numbers live here
 * and only here; the API, the pricing page and the checkout all read them.
 */

/** The price of one text, in cents: $1 = 50 texts. */
export const TEXT_PRICE_CENTS = 2

/** Free texts every account gets once, at its first sign-in ("your first $1 is on us"). */
export const WELCOME_TEXTS = 50

export type TextPack = {
  /** Stable id, sent by the buy flow and kept on the purchase. */
  id: string
  texts: number
  /** The price, before any sales tax added at checkout. */
  cents: number
}

/** The packs on sale: $5 / 250, $10 / 500, $20 / 1,000. No bulk discount yet. */
export const TEXT_PACKS: readonly TextPack[] = [
  { id: 'texts-250', texts: 250, cents: 500 },
  { id: 'texts-500', texts: 500, cents: 1000 },
  { id: 'texts-1000', texts: 1000, cents: 2000 },
]

export function packById(id: string): TextPack | undefined {
  return TEXT_PACKS.find((p) => p.id === id)
}

/**
 * The text credit terms the buyer accepts with the required checkbox, by version. Changing
 * the wording in /terms#text-credit or on the checkbox is a new version.
 */
export const CREDIT_TERMS_VERSION = 'credit-2026-10-25'

/** "Low" is at or below this share of the last pack bought (or of the welcome credit). */
export const LOW_BALANCE_FRACTION = 0.2

/** The balance at or below which texts are "low": 20% of the last top-up, rounded down. */
export function lowThreshold(lastTopUp: number): number {
  return Math.floor(lastTopUp * LOW_BALANCE_FRACTION)
}

export type CreditState = 'plenty' | 'low' | 'zero'

export function creditState(balance: number, lastTopUp: number): CreditState {
  if (balance <= 0) return 'zero'
  return balance <= lowThreshold(lastTopUp) ? 'low' : 'plenty'
}

/** "142 texts left", "1 text left", "0 texts left". Never negative on screen. */
export function textsLeft(balance: number): string {
  const n = Math.max(0, balance)
  return `${n.toLocaleString('en-US')} ${n === 1 ? 'text' : 'texts'} left`
}

/** "$10.00" */
export function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

/** The copy for the low and zero states (badge tooltip, banners, emails). §6.6. */
export const CREDIT_COPY = {
  low: 'Running low. Buy texts to keep texting; email always continues.',
  zero: 'Texts are paused. Updates still go out by email. Buy texts to turn texting back on.',
  lowBanner: (n: number) =>
    `You have ${textsLeft(n)}. When they run out, updates still go out by email.`,
  zeroBanner:
    'You’re out of texts. Everyone is still getting your updates by email. Buy texts to turn texting back on.',
  zeroBadge: 'Texts paused, email continues',
  welcome: 'Your first $1 is on us: 50 free texts to try on any event.',
  headline: 'Email updates are free. Texts are 2¢ each.',
  sub: 'Packs start at $5, work on any event and never expire.',
  logEmpty: 'No texts yet. Texts you send and credit you add will show up here.',
  /** Only while sales tax is on (TAX_MODE=stripe). */
  taxNote:
    'Prices exclude California sales tax, which is added at checkout for buyers in California.',
  taxLine: 'California sales tax (applies to California buyers)',
} as const

/** GET /api/account/credit: the header badge and the banners. */
export type CreditSummary = {
  balance: number
  /** The last pack bought, or the welcome credit if none: what "low" is measured against. */
  lastTopUp: number
  state: CreditState
}

export type CreditEntryKind =
  | 'welcome'
  | 'purchase'
  | 'debit'
  | 'credit_back'
  | 'chargeback'
  | 'adjustment'

/** One row of the text log (/account/texts). Phones are masked; no message bodies. */
export type CreditEntryDto = {
  id: number
  kind: CreditEntryKind
  /** + or -. */
  texts: number
  balanceAfter: number
  createdAt: number
  event: { id: number; title: string } | null
  /** Debits and credit-backs: "Dana R." (or "Removed person") and "•••• 4821". */
  recipient: { name: string; phone: string | null } | null
  messageKind: 'confirmation' | 'step' | null
  stepName: string | null
  /** Debits: 'credited_back' once a credit-back points at it. */
  status: 'sent' | 'credited_back' | null
  /** Purchases. */
  amountCents: number | null
  receiptUrl: string | null
  /** Credit-backs: the debit it reverses. */
  reversesId: number | null
  note: string | null
}

export type CreditLogPage = {
  entries: CreditEntryDto[]
  /** Pass as `before` for the next (older) page; null on the last page. */
  next: string | null
  /** The account's events, for the filter. */
  events: { id: number; title: string }[]
}

/** POST /api/credit/checkout */
export type CheckoutRequest = { pack: string; acceptTerms: boolean; termsVersion: string }
export type CheckoutResponse = { url: string }
